import { AssignmentStatus, Entity } from "@/enums";
import {
  PlannerCurriculum,
  PlannerCurriculumLesson,
  PlannerCurriculumTrack,
} from "@/features/planner/PlannerTypes";
import {
  Assignment,
  CurriculumLessonResponse,
  Story,
} from "@/models";
import moment from "moment";

export const calculatePercentageComplete = (
  studentAssignments: Assignment[],
  mainAssignment: Assignment,
  studentCount: number
) => {
  const storiesFinished = studentAssignments.reduce((acc, studentAssign) => {
    let count = 0;
    studentAssign.manifest.forEach((story) => {
      if (
        story.status === AssignmentStatus.COMPLETE ||
        story.status === AssignmentStatus.DONE ||
        story.status === AssignmentStatus.UNASSIGNED
      ) {
        count++;
      }
    });
    return acc + count;
  }, 0);
  // calculation assumes there is one student assignment for each student for one lesson
  const storiesAssigned =
    mainAssignment.manifest.length *
      (studentCount - studentAssignments.length) +
    studentAssignments.reduce((acc, cur) => {
      return acc + cur.manifest.length;
    }, 0);
  const completionPercentage =
    storiesAssigned > 0
      ? Math.floor((storiesFinished / storiesAssigned) * 100)
      : 0;
  return completionPercentage;
};

export const aggregateAssignmentsToLessons = (props: {
  dateFrom: string;
  dateTo: string;
  assignments: Assignment[];
  curriculumInfo: CurriculumLessonResponse[];
  stories: { [key: string]: Story };
  studentCount: number;
}): PlannerCurriculum | null => {
  const {
    assignments,
    dateFrom,
    dateTo,
    studentCount,
    curriculumInfo,
    stories,
  } = props;

  // console.log("=== aggregateAssignmentsToLessons ===");
  // console.log("Input assignments:", assignments);
  // console.log("Input curriculumInfo:", curriculumInfo);
  // console.log("Date range:", dateFrom, "to", dateTo);
  // console.log("Student count:", studentCount);

  if (!assignments.length) {
    console.log("No assignments provided, returning null");
    return null;
  }

  const curriculum: PlannerCurriculum = {
    name: "",
    id: "",
    dateFrom,
    dateTo,
    tracks: [],
  };

  // Some lessons can span multiple weeks, so we need to check if the assignment start or end date is between dateFrom and dateTo
  const currentWeekEndTime = new Date(dateTo).getTime() + 64 * 60 * 60 * 1000; // 64 hours after dateTo, because assignments are due Saturday not Friday
  const districtAssignments = assignments.filter(
    a =>
      a.entityType === Entity.DISTRICT &&
      (moment(a.dateFrom).isBetween(moment(dateFrom, 'YYYY-MM-DD').startOf('day'), currentWeekEndTime, null, '[]') ||
      moment(a.dateTo).isBetween(moment(dateFrom, 'YYYY-MM-DD').startOf('day'), currentWeekEndTime, null, '[]'))
  );
  const classAssignments = assignments.filter(
    a =>
      a.entityType === Entity.CLASS &&
      (moment(a.dateFrom).isBetween(moment(dateFrom, 'YYYY-MM-DD').startOf('day'), currentWeekEndTime, null, '[]') ||
      moment(a.dateTo).isBetween(moment(dateFrom, 'YYYY-MM-DD').startOf('day'), currentWeekEndTime, null, '[]'))
  );
  const outsideDateRangeClassAssignments = assignments.filter(
    a =>
      a.entityType === Entity.CLASS &&
      (moment(a.dateFrom).isAfter(currentWeekEndTime) ||
        moment(a.dateTo).isBefore(moment(dateFrom, 'YYYY-MM-DD').startOf('day')))
  );
  const studentAssignments = assignments.filter(
    a => a.entityType === Entity.STUDENT
  ).filter((assignment, index, array) => {
    // Find all assignments with the same lessonPlanId and entityId
    const duplicates = array.filter(a => 
      a.lessonPlanId === assignment.lessonPlanId && 
      a.entityId === assignment.entityId
    );
    
    // If this is the only one, keep it
    if (duplicates.length === 1) {
      return true;
    }
    
    // If there are duplicates, keep only the newest one by createdAt
    const newestDuplicate = duplicates.reduce((newest, current) => {
      const newestDate = new Date(newest.createdAt);
      const currentDate = new Date(current.createdAt);
      return currentDate > newestDate ? current : newest;
    });
    
    // Keep this assignment only if it's the newest one
    return assignment.assignmentId === newestDuplicate.assignmentId;
  });

  // console.log("=== Assignment Filtering ===");
  // console.log("Total assignments:", assignments.length);
  // console.log("Sample assignment:", assignments[0]);
  // console.log("All assignments:", assignments.map(a => ({
  //   entityType: a.entityType,
  //   dateFrom: a.dateFrom,
  //   dateTo: a.dateTo,
  //   lessonPlanId: a.lessonPlanId
  // })));
  // console.log("District assignments:", districtAssignments.length);
  // console.log("Class assignments:", classAssignments.length);
  // console.log("Outside date range class assignments:", outsideDateRangeClassAssignments.length);
  // console.log("Student assignments:", studentAssignments.length);
  // console.log("Current week end time:", new Date(currentWeekEndTime));
  // console.log("Date from:", new Date(dateFrom));
  // console.log("Date to:", new Date(dateTo));

  // Add detailed logging for district assignment filtering
  // console.log("=== District Assignment Filtering Details ===");
  assignments.filter(a => a.entityType === Entity.DISTRICT).forEach((assignment, index) => {
    const dateToTime = new Date(assignment.dateTo).getTime();
    const dateFromTime = new Date(assignment.dateFrom).getTime();
    const dateFromFilter = new Date(dateFrom).getTime();
    
    // console.log(`District Assignment ${index}:`, {
    //   lessonPlanId: assignment.lessonPlanId,
    //   dateFrom: assignment.dateFrom,
    //   dateTo: assignment.dateTo,
    //   dateToTime,
    //   currentWeekEndTime,
    //   dateFromTime,
    //   dateFromFilter,
    //   passesDateToFilter: dateToTime <= currentWeekEndTime,
    //   passesDateFromFilter: dateFromTime >= dateFromFilter,
    //   passesBothFilters: dateToTime <= currentWeekEndTime && dateFromTime >= dateFromFilter
    // });
  });

  // when a lesson date is changed to a date outside of dateFrom-dateTo,
  // we need to filter out DISTRICT assignments for the current dateFrom-dateTo period
  // if no matching CLASS assignments, use the DISTRICT assignments
  // else use the CLASS assignments.

  // replace district assignments with linked class assignments (assumes 1 class assign to 1 district assign)
  const mainAssignments = districtAssignments
    .filter(
      a =>
        (moment(a.dateFrom).isBefore(currentWeekEndTime) || // 64 hours after dateFrom, because assignments are due Saturday not Friday
        moment(a.dateTo).isBefore(currentWeekEndTime)) && // 64 hours after dateTo, because assignments are due Saturday not Friday
        !outsideDateRangeClassAssignments.find(c => c.lessonPlanId === a.lessonPlanId)
    )
    .map(d => {
      const classAssign = classAssignments.find(
        c => c.lessonPlanId === d.lessonPlanId
      );
      return classAssign ? classAssign : d;
    });
  
  const mainAssignmentsLessonPlanIds = mainAssignments.map(a => a.lessonPlanId);
  for (const assignment of classAssignments) {
    if (!mainAssignmentsLessonPlanIds.includes(assignment.lessonPlanId)) {
      // add CLASS assignments that are not in mainAssignments
      // this is for the case where a CLASS assignment date is in the future or past,
      // but the DISTRICT assignment date is in the past or future
      mainAssignments.push(assignment);
    }
  }

  // console.log("=== Main Assignments ===");
  // console.log("Main assignments after filtering:", mainAssignments.length);
  // console.log("Main assignments:", mainAssignments);
  // console.log("Main assignment lesson plan IDs:", mainAssignments.map(a => a.lessonPlanId));

  // aggregate tracks, units, lessons from district assignments that give us lesson plan ids
  const tracks = mainAssignments.reduce((acc, cur: any, index) => {
    // console.log(`Processing assignment ${index}:`, cur);
    
    const lessonCurInfo = curriculumInfo.find(
      ci => ci.lessonId === cur.lessonPlanId
    );

    // console.log(`Lesson curriculum info for ${cur.lessonPlanId}:`, lessonCurInfo);

    if (!lessonCurInfo) {
      // console.log("lessonCurInfo not found for lessonPlanId", cur.lessonPlanId);
      return acc;
    }

    if (!curriculum.name && lessonCurInfo) {
      curriculum.name = lessonCurInfo.curriculumLessonPlanDisplayName || lessonCurInfo.curriculumId; // Use display name, fallback to ID
    }

    if (!curriculum.id && lessonCurInfo) {
      curriculum.id = lessonCurInfo.curriculumId;
    }

    // reduce linked student assignments into teacher created and student created
    const filteredStudentAssignments = studentAssignments.filter(
      a => a.parentId && (a.parentId === cur.assignmentId || a.parentId === cur.parentId)
    );

    // completion percentage aggregation
    const completionPercentage = calculatePercentageComplete(
      filteredStudentAssignments,
      cur,
      studentCount
    );

    const lesson = {
      id: cur.assignmentId,
      duration: cur.duration,
      dateFrom: cur.dateFrom,
      dateTo: cur.dateTo,
      mainAssignment: { ...structuredClone({...cur, lessonName: lessonCurInfo ? lessonCurInfo.lessonName : "", resources: cur.resources || [] }) },
      studentAssignments: filteredStudentAssignments,
      completionPercentage,
      lessonPlanId: cur.lessonPlanId,
      lessonName: lessonCurInfo? lessonCurInfo.lessonName : "",
      name: lessonCurInfo ? lessonCurInfo.lessonDisplayName : "",
      description: lessonCurInfo ? lessonCurInfo.lessonDescription : "",
      resources: [],
      skills: lessonCurInfo ? lessonCurInfo.skills : [],
      timeAssigned: {},
      curriculumName: lessonCurInfo ? lessonCurInfo.curriculumLessonPlanDisplayName : "",
      curriculumId: lessonCurInfo ? lessonCurInfo.curriculumId : "",
    };

    const trackId = lessonCurInfo ? lessonCurInfo.trackId : "";
    const track = acc.find(t => t.id === trackId);
    const unit = {
      id: `${lessonCurInfo?.unitId}`,
      name: `${lessonCurInfo?.unitDisplayName}`,
      lessons: [lesson],
      sequence: lessonCurInfo?.lessonSequence
        ? [lessonCurInfo?.lessonSequence]
        : [], // TODO talk to Matt about getting unit sequence in the response instead of array of lesson sequences
    };
    if (!track) {
      acc.push({
        id: trackId,
        name: `${lessonCurInfo?.trackDisplayName}`,
        units: [unit],
      });
    } else {
      const foundUnit = track.units.find(u => u.id === unit.id);
      if (!foundUnit) {
        track.units.push(unit);
      } else {
        foundUnit.lessons.push(lesson);
        // TODO won't need to do the following if we get back unit sequence as part of curriculum info response
        if (lessonCurInfo?.lessonSequence !== undefined) {
          foundUnit.sequence.push(lessonCurInfo.lessonSequence);
        }
      }
    }

    // sort units by sequence, then lessons by district assignment dateFrom
    // then by lessons lowest skill sequence number
    acc.forEach(track => {
      track.units.forEach(unit => {
        unit.lessons.sort((a, b) => {
          const mainAssignA = mainAssignments.find(
            district => a.lessonPlanId === district.lessonPlanId
          );
          const mainAssignB = mainAssignments.find(
            district => b.lessonPlanId === district.lessonPlanId
          );

          const dateComparison =
            `${mainAssignA?.dateFrom.split(" ")[0]}`.localeCompare(
              `${mainAssignB?.dateFrom.split(" ")[0]}`
            );

          if (dateComparison !== 0) {
            return dateComparison;
          }

          if (!a.skills.length || !b.skills.length) return 0;
          const sortedSkillsA = [...a.skills].sort((a, b) =>
            a.sequence && b.sequence ? a.sequence - b.sequence : 0
          );
          const sortedSkillsB = [...b.skills].sort((a, b) =>
            a.sequence && b.sequence ? a.sequence - b.sequence : 0
          );
          if (!sortedSkillsA[0].sequence || !sortedSkillsB[0].sequence)
            return 0;
          return sortedSkillsA[0].sequence - sortedSkillsB[0].sequence;
        });
      });
      // TODO sort units by lowest lesson sequence, but eventually just by unit sequence value
      track.units.sort((a, b) => {
        if (!a.sequence.length || !b.sequence.length) return 0;
        a.sequence.sort();
        b.sequence.sort();
        return a.sequence[0] - b.sequence[0];
      });
    });

    return acc;
  }, [] as PlannerCurriculumTrack[]);

  curriculum.tracks = tracks;

  return curriculum;
};

export const aggregateUpdatedLessonByPlanId = (
  fetchedAssignments: Assignment[],
  currentCurriculum: PlannerCurriculum,
  lessonPlanId: string,
  studentCount: number
): {
  lesson: PlannerCurriculumLesson;
  trackId: string;
  unitId: string;
} | null => {
  if (fetchedAssignments.length) {
    const filteredAssignments = fetchedAssignments.filter(
      (assign) => assign.lessonPlanId === lessonPlanId
    );
    currentCurriculum.tracks.forEach((t) => {
      t.units.forEach((u) => {
        u.lessons.forEach((l) => {
          if (l.lessonPlanId === lessonPlanId) {
            const lesson = l;
            const mainAssignment = filteredAssignments.filter(
              (a) => a.entityType === Entity.CLASS
            );
            const studentAssignments = filteredAssignments.filter(
              (a) => a.entityType === Entity.STUDENT
            );
            if (mainAssignment.length) {
              const { assignmentId, dateFrom, dateTo } = mainAssignment[0];
              if (assignmentId && dateFrom && dateTo) {
                lesson.id = assignmentId;
                lesson.dateFrom = dateFrom;
                lesson.dateTo = dateTo;
              }
              lesson.mainAssignment = mainAssignment[0];
            }
            lesson.studentAssignments = studentAssignments;
            lesson.completionPercentage = calculatePercentageComplete(
              studentAssignments,
              mainAssignment.length ? mainAssignment[0] : l.mainAssignment,
              studentCount
            );

            return { lesson, trackId: t.id, unitId: u.id };
          }
        });
      });
    });
  }

  return null;
};

export default aggregateAssignmentsToLessons;
